import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { createBaseApiWithAuth } from "@/utilities/reduxToolkit";

/**
 * Lists API — REP-200 §2 + §3.
 *
 * Polymorphic across PROPERTY / PERSON / DOCUMENT_TEMPLATE / CLIENT_NEED.
 * Three flavors of list:
 *   - FAVORITES: singleton per (owner, entityType), lazy-created
 *   - DYNAMIC:   saved search — items are evaluated server-side
 *   - STATIC:    explicit list_item rows
 */

export type ListEntityType =
  | "PROPERTY"
  | "PERSON"
  | "DOCUMENT_TEMPLATE"
  | "CLIENT_NEED";

export type ListKind = "STATIC" | "DYNAMIC" | "FAVORITES";

export interface List {
  id: string;
  ownerId: string;
  listType: ListKind;
  entityType: ListEntityType;
  name: string;
  description: string | null;
  query: Record<string, any> | null;
  shareToken: string | null;
  sharedAt: string | null;
  createdAt: string;
  updatedAt: string;
  active: boolean;
  _count?: { items: number };
  items?: any[];
}

export const listsApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "listsApi",
    baseUrl: "/api/lists",
  }),
  reducerPath: "listsApi",
  tagTypes: ["Lists", "List", "Favorites"],
  endpoints: (builder) => ({
    /* GET /lists?entityType=&listType= */
    getLists: builder.query<
      { data: List[] },
      { entityType?: ListEntityType; listType?: ListKind } | void
    >({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.entityType) params.set("entityType", args.entityType);
        if (args?.listType) params.set("listType", args.listType);
        const qs = params.toString();
        return qs ? `?${qs}` : "";
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map((l) => ({ type: "List" as const, id: l.id })),
              "Lists",
            ]
          : ["Lists"],
    }),

    /* GET /lists/:id?include=items|count */
    getListById: builder.query<
      { data: List },
      { id: string; include?: "items" | "count" }
    >({
      query: ({ id, include }) => `/${id}${include ? `?include=${include}` : ""}`,
      providesTags: (_result, _err, { id }) => [{ type: "List" as const, id }],
    }),

    /* POST /lists */
    createList: builder.mutation<
      { data: List },
      {
        listType: "STATIC" | "DYNAMIC";
        entityType: ListEntityType;
        name: string;
        description?: string;
        query?: Record<string, any>;
      }
    >({
      query: (body) => ({ url: "", method: "POST", body }),
      invalidatesTags: ["Lists"],
    }),

    /* PATCH /lists/:id */
    updateList: builder.mutation<
      { data: List },
      {
        id: string;
        name?: string;
        description?: string | null;
        query?: Record<string, any>;
      }
    >({
      query: ({ id, ...body }) => ({ url: `/${id}`, method: "PATCH", body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "List" as const, id }, "Lists"],
    }),

    /* DELETE /lists/:id */
    deleteList: builder.mutation<{ message: string }, { id: string }>({
      query: ({ id }) => ({ url: `/${id}`, method: "DELETE" }),
      invalidatesTags: ["Lists"],
    }),

    /* POST /lists/:id/items */
    addListItems: builder.mutation<
      { data: { added: number } },
      { id: string; entityIds: string[] }
    >({
      query: ({ id, entityIds }) => ({
        url: `/${id}/items`,
        method: "POST",
        body: { entityIds },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "List" as const, id }, "Lists"],
    }),

    /* DELETE /lists/:id/items/:entityId */
    removeListItem: builder.mutation<
      { message: string },
      { id: string; entityId: string }
    >({
      query: ({ id, entityId }) => ({
        url: `/${id}/items/${entityId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "List" as const, id }, "Lists"],
    }),

    /* POST /lists/:id/share */
    enableListShare: builder.mutation<{ data: List }, { id: string }>({
      query: ({ id }) => ({ url: `/${id}/share`, method: "POST" }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "List" as const, id }, "Lists"],
    }),

    /* DELETE /lists/:id/share */
    disableListShare: builder.mutation<{ data: List }, { id: string }>({
      query: ({ id }) => ({ url: `/${id}/share`, method: "DELETE" }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "List" as const, id }, "Lists"],
    }),

    /* POST /lists/:id/apply-to-deal */
    applyListToDeal: builder.mutation<
      { data: { dealId: string; created: any[] } },
      { id: string; dealId: string }
    >({
      query: ({ id, dealId }) => ({
        url: `/${id}/apply-to-deal`,
        method: "POST",
        body: { dealId },
      }),
    }),

    /* POST /lists/:id/apply-to-need */
    applyListToNeed: builder.mutation<
      { data: { clientNeedId: string; attached: number } },
      { id: string; clientNeedId: string }
    >({
      query: ({ id, clientNeedId }) => ({
        url: `/${id}/apply-to-need`,
        method: "POST",
        body: { clientNeedId },
      }),
    }),
  }),
});

/* Public read-only — no auth */
export const publicListsApi = createApi({
  reducerPath: "publicListsApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/api/lists/public" }),
  endpoints: (builder) => ({
    getPublicList: builder.query<{ data: List }, { shareToken: string }>({
      query: ({ shareToken }) => `/${shareToken}`,
    }),
  }),
});

/* Favorites — separate base URL since the route lives at /api/favorites */
export const favoritesApi = createApi({
  ...createBaseApiWithAuth({
    reducerPath: "favoritesApi",
    baseUrl: "/api/favorites",
  }),
  reducerPath: "favoritesApi",
  tagTypes: ["FavoritesList"],
  endpoints: (builder) => ({
    getFavoritesList: builder.query<
      { data: List },
      { entityType: ListEntityType }
    >({
      query: ({ entityType }) => `/${entityType}`,
      providesTags: (_r, _e, { entityType }) => [
        { type: "FavoritesList" as const, id: entityType },
      ],
    }),
    addFavorite: builder.mutation<
      { data: { list: List; isFavorite: boolean } },
      { entityType: ListEntityType; entityId: string }
    >({
      query: ({ entityType, entityId }) => ({
        url: `/${entityType}/${entityId}`,
        method: "POST",
      }),
      invalidatesTags: (_r, _e, { entityType }) => [
        { type: "FavoritesList" as const, id: entityType },
      ],
    }),
    removeFavorite: builder.mutation<
      { data: { isFavorite: boolean } },
      { entityType: ListEntityType; entityId: string }
    >({
      query: ({ entityType, entityId }) => ({
        url: `/${entityType}/${entityId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, { entityType }) => [
        { type: "FavoritesList" as const, id: entityType },
      ],
    }),
  }),
});

export const {
  useGetListsQuery,
  useGetListByIdQuery,
  useCreateListMutation,
  useUpdateListMutation,
  useDeleteListMutation,
  useAddListItemsMutation,
  useRemoveListItemMutation,
  useEnableListShareMutation,
  useDisableListShareMutation,
  useApplyListToDealMutation,
  useApplyListToNeedMutation,
} = listsApi;

export const { useGetPublicListQuery } = publicListsApi;

export const {
  useGetFavoritesListQuery,
  useAddFavoriteMutation,
  useRemoveFavoriteMutation,
} = favoritesApi;
